import { useQuery } from '@tanstack/react-query';
import { breakglassApi } from '@/lib/api';
import { useAuthStore } from '@/hooks/useAuthStore';

// ─────────────────────────────────────────────────────────────────────────────
// C13 R14 — role gate (defense-in-depth)
// ─────────────────────────────────────────────────────────────────────────────
// Backend enforces SUPER_ADMIN on every breakglass endpoint (primary guard).
// This page is read-only today (no writeable controls), so the UI has nothing
// to disable.  Future R10 Breakglass request/approve forms (deferred C15) MUST
// honor `isSuperAdmin` via `disabled` attrs on mutations to match backend 403
// as a UX hint; backend remains the authoritative guard.
// ─────────────────────────────────────────────────────────────────────────────

interface BreakglassAction {
  action: string;
  level: 'STANDARD' | 'ELEVATED' | 'CRITICAL';
  description: string;
}

interface BreakglassLog {
  id: string;
  action: string;
  targetType: string;
  targetId: string | null;
  severity: string;
  details: {
    level: string;
    reason: string;
    confirmationId: string;
    description: string;
  };
  staff: {
    email: string;
    nombre: string;
  };
  ipAddress: string | null;
  createdAt: string;
}

interface BreakglassStats {
  total: number;
  last24h: number;
  last7d: number;
  byAction: Array<{ action: string; count: number }>;
  bySeverity: Array<{ severity: string; count: number }>;
}

export default function Breakglass() {
  // C13 R14 — defense-in-depth: read role for future write controls.  Even
  // though the current page is read-only, we surface a visible notice when a
  // non-SUPER_ADMIN reaches it, so operators know they cannot perform future
  // write actions (request/approve/deny) planned for C15.
  const { staff } = useAuthStore();
  const isSuperAdmin = staff?.role === 'SUPER_ADMIN';

  // Fetch actions
  const { data: actionsData } = useQuery({
    queryKey: ['breakglass-actions'],
    queryFn: async () => {
      const response = await breakglassApi.getActions();
      return response.data as BreakglassAction[];
    },
  });

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['breakglass-stats'],
    queryFn: async () => {
      const response = await breakglassApi.getStats();
      return response.data as BreakglassStats;
    },
  });

  // Fetch history
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['breakglass-history'],
    queryFn: async () => {
      const response = await breakglassApi.getHistory({ limit: 50 });
      return response.data as {
        logs: BreakglassLog[];
        pagination: { total: number; limit: number; offset: number };
      };
    },
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('es-MX', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800';
      case 'ELEVATED':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // C13 R15 — backend AuditSeverity enum is {INFO, WARNING, CRITICAL}.
  // HIGH/MEDIUM branches were unreachable dead code (pre-C12 R1 cleanup).
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800';
      case 'WARNING':
        return 'bg-yellow-100 text-yellow-800';
      case 'INFO':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Break-glass</h1>
        <p className="text-gray-600">Control de acciones peligrosas que requieren confirmacion adicional</p>
      </div>

      {/* C13 R14 — role hint (defense-in-depth; backend enforces 403) */}
      {!isSuperAdmin && (
        <div
          role="status"
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800"
        >
          Solo los SUPER_ADMIN pueden ejecutar acciones break-glass. Esta vista es solo lectura.
        </div>
      )}

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      ) : statsData ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Eventos Break-glass</p>
                <p className="text-2xl font-bold text-gray-900">{statsData.total}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <ShieldIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ultimas 24 horas</p>
                <p className="text-2xl font-bold text-gray-900">{statsData.last24h}</p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <ClockIcon className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Ultimos 7 dias</p>
                <p className="text-2xl font-bold text-gray-900">{statsData.last7d}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <CalendarIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Protected Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Acciones Protegidas</h2>
            <p className="text-sm text-gray-500">Operaciones que requieren confirmacion break-glass</p>
          </div>
          <div className="divide-y divide-gray-200">
            {actionsData?.map((action) => (
              <div key={action.action} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{action.action}</p>
                    <p className="text-sm text-gray-500">{action.description}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getLevelColor(action.level)}`}>
                    {action.level}
                  </span>
                </div>
              </div>
            ))}
            {(!actionsData || actionsData.length === 0) && (
              <div className="px-6 py-8 text-center text-gray-500">
                No hay acciones protegidas configuradas
              </div>
            )}
          </div>
        </div>

        {/* Stats by Action */}
        {statsData && statsData.byAction.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Uso por Accion</h2>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {statsData.byAction.slice(0, 10).map((item) => (
                  <div key={item.action} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{item.action}</span>
                    <div className="flex items-center">
                      <div className="w-32 h-2 bg-gray-200 rounded-full mr-3">
                        <div
                          className="h-2 bg-red-500 rounded-full"
                          style={{
                            width: `${(item.count / statsData.total) * 100}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8 text-right">
                        {item.count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* History Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Historial de Break-glass</h2>
          <p className="text-sm text-gray-500">Registro de todas las acciones break-glass ejecutadas</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accion</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ejecutado por</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Objetivo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Razon</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nivel</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {historyLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : historyData?.logs && historyData.logs.length > 0 ? (
                historyData.logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{log.action}</div>
                      <div className="text-xs text-gray-500">{log.details?.description}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{log.staff?.nombre}</div>
                      <div className="text-xs text-gray-500">{log.staff?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.targetType}
                      {log.targetId && (
                        <span className="ml-1 text-xs font-mono bg-gray-100 px-1 rounded">
                          {log.targetId.slice(0, 8)}...
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 max-w-xs truncate" title={log.details?.reason}>
                        {log.details?.reason}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        {log.details?.confirmationId}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getSeverityColor(log.severity)}`}>
                        {log.details?.level || log.severity}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No hay eventos break-glass registrados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <InfoIcon className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Como funciona el Break-glass</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><strong>STANDARD:</strong> Requiere una justificacion escrita (minimo 10 caracteres)</li>
              <li><strong>ELEVATED:</strong> Requiere justificacion + puede requerir aprobacion de supervisor</li>
              <li><strong>CRITICAL:</strong> Requiere justificacion + re-verificacion MFA</li>
            </ul>
            <p className="mt-2">
              Todas las acciones break-glass se registran en el audit log para revision posterior.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
